import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import AOS from "aos";

function TermsOfUse() {
  const [language, setLanguage] = useState("english");

  useEffect(() => {
    AOS.refresh();
  }, []);

  const content = {
    english: {
      title: "Terms of Use",
      homeButton: "Home",
      lastUpdated: "Last updated:",
      effectiveDate: "November 17, 2025",

      // Introduction
      intro1: `PLEASE READ THESE TERMS OF USE CAREFULLY BEFORE USING iNutriCal products (INCLUDING the Website, the web application, and the mobile application)`,
      intro2: `THIS WEBSITE, https://inutrical.net/login (the "Website") and its corresponding mobile application (the "App"), are owned and operated by Dalalat For Information Technology (the "Company", "Corporation", "we", "us", "our"). Using our Website (whether as a guest or registered user) includes accessing, browsing, searching, using our forums, using any service we may provide, or registering to use the Website.`,
      intro3: `Any use of this Website is subject to and conditioned upon your assent to and compliance with all of the terms and conditions in this Terms of Use agreement (the "Agreement"), which also incorporates our Privacy Notice, as appears in our Privacy Policy. By using the Website, you signify your consent to be bound by this Agreement. If you do not agree with any of the "Terms of Use" or if you are not of legal age to form a binding contract, please do not use this Website and do not use or access our Services.`,
      intro4: `We may amend this Agreement at any time by posting the amended terms on the Website due to changes in the Website's content or the addition of new features. It is therefore essential that you refer to these Terms of Use from time to time to ensure that you are familiar with the relevant terms which will govern your use of the Website. Otherwise, your continued use of the Website shall constitute your consent to any changes made. If you do not agree to the new or different terms, you should not use the Website. We reserve the right to change these Terms of Use (and any other terms, conditions, and notices) under which this Website is offered. This version of the Terms of Use is effective as of November 17, 2025.`,

      // Warning Notice
      warning: `BY USING THIS WEBSITE OR ANY SERVICES PROVIDED THROUGH IT, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS OF USE. IF YOU DO NOT AGREE WITH ANY PART OF THESE TERMS, YOU MUST NOT USE THE WEBSITE OR ANY RELATED SERVICES.`,

      // Content Section
      contentTitle: "Content",
      contentItems: [
        "The contents of the Website are for informational purposes only. The contents of the Website and the App are not intended to substitute professional medical advice, diagnosis, treatment, or nutritional assessments.",
        "The Company does not recommend or endorse any specific tests, physicians, products, procedures, opinions, or other information that may be mentioned on the Website (including those promoted by any advertisers or advertising sponsors).",
        "Although we make reasonable efforts to update the information on our Website, we make no representations, warranties or guarantees, whether express or implied, that the content on our Website is accurate, complete or up-to-date.",
        "Reliance on any information provided on the Website is solely at your own risk. We also do not warrant or represent that the information or materials available through the Website will meet your particular requirements or needs or that access to, or use of, the same will be uninterrupted or completely secure.",
        "If you wish to use any content on our Website other than as set out above, please contact us via the 'Contact us' button.",
        "The 'more info' list in the Software that contains specific information about each product was obtained as is from the product's webpage on the producing Company's Website. We can accept no responsibility in respect of any scientific inaccuracies in the information contained there.",
        "Other content generated by us on the Website (including the web application and the mobile App) may contain technical inaccuracies or typographical errors. We reserve the right to make changes and improvements to any information on the Website at any time and without notice.",
      ],

      // Accessing the Website or the App
      accessTitle: "Accessing the Website or the App",
      accessItems: [
        "We do not guarantee that our Website, or any content on it, will always be available or that access to it will be uninterrupted.",
        "We may suspend, withdraw, discontinue, or change all or any part of our Website without notice.",
        "We will not be liable to you if, for any reason, our Website (or any part, features, or services under the Website) is removed or is unavailable at any time or for any period.",
        "Registered users will be notified by email if such an interruption will affect the service to which they subscribed.",
        "You are responsible for making all arrangements necessary for you to access our Website (including having an internet connection and an appropriate browser).",
        "You are also responsible for ensuring that everyone who accesses our Website through your internet connection is aware of these Terms of Use and other applicable terms and conditions, and that they comply with them.",
      ],

      // Prohibited Uses
      prohibitedTitle: "Prohibited Uses",
      prohibitedIntro:
        "You may use our Website and associated services only for lawful purposes. You may not use our Website:",
      prohibitedItems: [
        "In any way that breaches these Terms of Use or any applicable laws or regulations.",
        "In any way that is unlawful or fraudulent, or which has any unlawful or fraudulent purpose or effect.",
        "To use or re-use any material for any commercial purpose or in a manner that does not comply with our Terms of Use statement.",
        "To transmit, or procure the sending of, any unsolicited or unauthorized advertising or promotional material or any other form of similar request (commonly known as 'spam').",
        "To knowingly transmit any data, send or upload any material that contains viruses, Trojan horses, worms, time-bombs, keystroke loggers, spyware, adware, or any other harmful programs or similar computer code designed to affect the operation of any computer software or hardware adversely.",
      ],

      // Disclaimers
      disclaimersTitle: "Disclaimers",
      disclaimersItems: [
        "The Website contains content generated by us (or on our behalf by scientifically qualified authors). The content on the Website is for general information purposes only. It is not set up to provide specific advice that you should rely on.",
        "In particular, the Website (and the information accessible through it) is not a substitute for professional medical advice from a qualified doctor, clinical dietitian, or other healthcare professionals.",
        "As a clinical dietician, you are obligated to consult the results generated by our web application with your team of dieticians and physicians.",
        "If you are a healthcare professional, then this information (including any professional reference material or suggested diet plans) is intended to support, not replace, your own knowledge, experience, and judgment.",
      ],

      // Intellectual Property Rights
      ipTitle: "Intellectual Property Rights",
      ipItems: [
        "We are the owner of all intellectual property rights in our Website, the material published on it, and the services accessed through it. Copyright laws and treaties protect those works in the Kingdom of Saudi Arabia.",
        "You may download and/or print off one copy of extracts of any page(s) from our Website for your professional use, and you may (in a non-commercial context) draw the attention of others to content posted on our Website.",
        "You must not modify the paper or digital copies of any materials you have printed off or downloaded in any way, and you must not further use any illustrations, logos, photographs, video or audio sequences, or any graphics separately from any accompanying text.",
        "You must not use any part of the content on our Website for commercial purposes without obtaining a license to do so from us.",
        "If you print off, copy, or download any part of our Website in breach of these Terms of Use, your right to use our Website (and any related materials) will cease immediately. You must, at our choice, return or destroy any copies of the materials you have made (and these Terms of Use shall continue to apply in respect of any such materials).",
      ],

      // Registering for an Account
      accountTitle: "Registering for an Account",
      accountItems: [
        "If you choose to register for an account on our Website, and you select or are given a user identification code, password, or any other piece of information as part of our registration or security procedures, you must treat such information as confidential. You must not disclose it to anyone.",
        "We have the right to deactivate any user identification code or password, whether chosen by you or allocated by us, at any time (and so remove your ability to use our services) at our discretion, including, without limitation, in circumstances where, in our reasonable opinion, you have failed to comply with any of the provisions of these Terms of Use.",
        "If you know or suspect that anyone other than you has access to your user identification code or password, you must promptly reset your password to something that only you know.",
        "Please see our Privacy Policy for more information regarding the use of personal data relating to you.",
        "You must only use the Website for lawful purposes, and you must not infringe the rights of other users of this site or prevent their enjoyment of it.",
      ],

      // Suspension and Termination
      suspensionTitle: "Suspension and Termination of Access",
      suspensionIntro:
        "We will determine, in our discretion, whether there has been a breach of our Terms of Use through your use of our Website. When a breach of these terms has occurred, or where we otherwise deem it necessary, then we may take such action as we deem appropriate, which may include:",
      suspensionItems: [
        "Issuing a warning to your institution.",
        "Immediate, temporary, or permanent withdrawal of your right to use our Web application and the mobile App.",
        "Legal proceedings against you for reimbursement of all costs on an indemnity basis (including, but not limited to, reasonable administrative and legal costs) resulting from the breach.",
        "Disclosure of such information to law enforcement authorities as we reasonably feel is necessary.",
      ],
      suspensionEnd:
        "We exclude liability for actions taken in response to breaches of this acceptable use policy. The responses described in this policy are examples, and we may take such other actions as we may reasonably deem appropriate.",

      // Contributions
      contributionsTitle: "Contributions to our Web Application",
      contributionsItems: [
        "Any contribution to the Web application, including submitting new diseases and medications, you grant us a perpetual, royalty-free, non-exclusive right to store, modify (after checking the scientific accuracy of your submission), copy, and publish the contribution on the Web application and the mobile App and to make it available to other users.",
        "We have the right to remove any posting you make on our Web application if your post was not supported by solid scientific evidence, did not comply with the content standards, or any terms set out in this Agreement.",
      ],

      // Third-Party Links
      linksTitle: "Third-Party Links and Resources on our Website",
      linksItems: [
        "The Website may contain links to websites that our Company does not maintain. Links to third-party websites are provided for your convenience and information only.",
        "Third-party websites are not under our Company's control, and we are not responsible for the content or accuracy of those sites, or for the products or services offered on or through them.",
        "The inclusion of a link on the Website does not imply our endorsement of the third-party Website, nor does it imply that our Company is affiliated with the owners or sponsors of the third-party Website.",
        "Any information or content that our Company makes available on this Website from third parties ('Third Party Materials') is provided for general information purposes only.",
        "The Company does not endorse Third-Party Materials and accepts no responsibility for and disclaims all liability in connection with such Third-Party Materials.",
      ],

      // Limitations of Liability
      liabilityTitle: "Limitations of Liability",
      liabilityIntro:
        "Please read this section carefully as it sets out the limits of our liability to you in relation to your use of iNutriCal (INCLUDING the Website, the web application, and the mobile application).",
      liabilityImportant:
        "Nothing in these Terms of Use excludes or limits our liability for death or personal injury arising from our negligence, or for our fraud or fraudulent misrepresentation, or for any other liability that cannot be excluded or limited by the Kingdom of Saudi Arabia law.",
      liabilityNote:
        "Please note that we provide our product as a reference and support tool for use only by healthcare professionals.",
      liabilityItems: [
        "To the extent permitted by law, we exclude all conditions, warranties, representations or other terms which may apply to the Website or any content on it, whether express or implied.",
        "You agree not to use the Website (or any of its content) for any commercial or business purposes, and we have no liability to you for any loss of profit, loss of business, business interruption, or loss of business opportunity.",
        "We assume no responsibility for the content of websites linked on our Website. Such links should not be interpreted as endorsement by us of those linked websites. We will not be liable for any loss or damage that may arise from your use of them.",
        "We use our reasonable efforts to check the accuracy of the professional information published on the Website. You should note that we do not warrant that such information will be error-free, and you acknowledge that the information, products, and services published on this Website, the web application, or the mobile App may include inaccuracies or typographical errors.",
        "Changes are periodically made to the information on our Website. YOU MUST ALWAYS RELY ON YOUR EXPERT JUDGMENT IN DEALING WITH PATIENTS. If you spot any such inaccuracies or have scientific suggestions, it is your absolute responsibility to notify us ASAP via the 'Contact Us' button.",
        "We will not be liable for any loss or damage caused by a virus, distributed denial-of-service attack, or other technologically harmful material that may infect your computer equipment, computer programs, data, or other proprietary material due to your use of our Website or to your downloading of any content on it, or on any website linked to it.",
        "We will also not be liable for other technological issues that may affect your access to our services, such as the unavailability of agreed-on servers or internet service.",
      ],
      liabilityEnd:
        "This section does not affect any legal rights you may have as a consumer. You acknowledge that we have made the Website available to you in reliance upon these Terms of Use and, in particular, these Limitations of Liability.",

      // Collection of Personal Data
      dataTitle: "Collection of Personal Data",
      dataItems: [
        "If you register to use our Web App, or any service(s) accessible through our Website, we may collect personal data from you (including, without limitation, your name, contact phone number, and a valid email address).",
        "These data will be securely stored in accordance with our Privacy Policy.",
        "Patient personal and medical data will be securely stored in accordance with our Privacy Policy.",
        "If any third parties are involved in the delivery or operation of any services you may choose to receive, this will be detailed in the information and terms provided to you at the time of signing the contract.",
      ],

      // Viruses
      virusesTitle: "Viruses",
      virusesItems: [
        "We do not guarantee that our Website will be secure or free from bugs or viruses, and you should use your own, up-to-date antivirus software.",
        "You must not misuse our Website by knowingly introducing viruses, trojans, worms, logic bombs, or any other material which is malicious or technologically harmful.",
        "You must not attempt to gain unauthorized access to our Website, the server on which our Website is stored, or any server, computer, or database connected to our Website or applications associated with it.",
        "You must not attack our Website via a denial-of-service attack or a distributed denial-of-service attack.",
        "By breaching this provision, you may be committing a criminal offence under the Communications and Information Technology Commission Act, Kingdom of Saudi Arabia.",
        "We will report any such breach to the relevant law enforcement authorities and will co-operate with them by disclosing your identity. In the event of such a breach, your right to use our Website will cease immediately.",
      ],

      // General
      generalTitle: "General",
      generalItems: [
        "If any part of these Terms of Use is determined to be invalid or unenforceable pursuant to applicable law including, but not limited to, the disclaimers and liability limitations above, then the invalid or unenforceable provision will be deemed superseded by a valid, enforceable provision that most closely matches the intent of the original provision and the remainder of the Agreement shall continue in effect.",
        "We reserve the right to freely assign, or otherwise transfer, any of our rights or obligations under these Terms of Use to any third party at our discretion.",
        "We will not be in breach of any of our obligations under these Terms of Use (or otherwise liable for any failure or delay in performance) if we are prevented, hindered, or delayed in, or from, performing any of our obligations by any event beyond our reasonable control. The time for performance of such obligations shall be extended accordingly.",
        "Subject to any specific terms detailed on the Website in relation to particular features or materials, these Terms of Use (together with the Privacy Policy) set out the entire Agreement between you and us in respect of your use of our Website or services.",
        "Any rights not expressly granted herein are reserved.",
      ],

      // Contact Us
      contactTitle: "Contact Us",
      contactContent:
        "To contact us, please use the contact us form on the iNutriCal Website.",
    },
    arabic: {
      title: "شروط الاستخدام",
      homeButton: "الصفحة الرئيسية",
      lastUpdated: "آخر تحديث:",
      effectiveDate: "17 نوفمبر 2025",

      // Introduction
      intro1: `يرجى قراءة شروط الاستخدام هذه بعناية قبل استخدام منتجات iNutriCal (بما في ذلك الموقع الإلكتروني، والتطبيق الويب، والتطبيق المحمول)`,
      intro2: `هذا الموقع الإلكتروني، https://inutrical.net/login ("الموقع الإلكتروني") وتطبيقه المحمول المقابل ("التطبيق")، مملوكان وتشغلهما دلالة لتقنية المعلومات ("الشركة"، "المؤسسة"، "نحن"، "خاصتنا"). يشمل استخدام موقعنا الإلكتروني (سواء كضيف أو مستخدم مسجل) الوصول إليه، وتصفحه، والبحث فيه، واستخدام منتدياتنا، واستخدام أي خدمة نقدمها، أو التسجيل لاستخدام الموقع الإلكتروني.`,
      intro3: `أي استخدام لهذا الموقع الإلكتروني يخضع ويتوقف على موافقتك وامتثالك لجميع الشروط والأحكام في اتفاقية شروط الاستخدام هذه ("الاتفاقية")، والتي تشمل أيضًا إشعار الخصوصية الخاص بنا، كما يظهر في سياسة الخصوصية الخاصة بنا. باستخدام الموقع الإلكتروني، فإنك توافق على الالتزام بهذه الاتفاقية. إذا كنت لا توافق على أي من "شروط الاستخدام" أو إذا لم تكن في السن القانوني لإبرام عقد ملزم، فيرجى عدم استخدام هذا الموقع الإلكتروني وعدم استخدام أو الوصول إلى خدماتنا.`,
      intro4: `يجوز لنا تعديل هذه الاتفاقية في أي وقت عن طريق نشر الشروط المعدلة على الموقع الإلكتروني بسبب تغييرات في محتوى الموقع الإلكتروني أو إضافة ميزات جديدة. لذلك من الضروري أن تراجع شروط الاستخدام هذه من وقت لآخر للتأكد من أنك على دراية بالشروط ذات الصلة التي ستتحكم في استخدامك للموقع الإلكتروني. وإلا فإن استمرارك في استخدام الموقع الإلكتروني سيشكل موافقتك على أي تغييرات يتم إجراؤها. إذا كنت لا توافق على الشروط الجديدة أو المختلفة، فلا يجب عليك استخدام الموقع الإلكتروني. نحتفظ بالحق في تغيير شروط الاستخدام هذه (وأي شروط أو أحكام أو إشعارات أخرى) التي يقدم بموجبها هذا الموقع الإلكتروني. هذا الإصدار من شروط الاستخدام ساري اعتبارًا من 17 نوفمبر 2025.`,

      // Warning Notice
      warning: `باستخدامك لهذا الموقع الإلكتروني أو أي خدمات يتم تقديمها من خلاله، فإنك تقر بأنك قد قرأت وفهمت وتوافق على الالتزام بشروط الاستخدام هذه. إذا كنت لا توافق على أي جزء من هذه الشروط، فلا يجب عليك استخدام الموقع الإلكتروني أو أي خدمات مرتبطة به.`,

      // Content Section
      contentTitle: "المحتوى",
      contentItems: [
        "محتويات الموقع الإلكتروني هي لأغراض إعلامية فقط. محتويات الموقع الإلكتروني والتطبيق لا تهدف إلى أن تكون بديلاً عن المشورة الطبية المهنية، أو التشخيص، أو العلاج، أو التقييمات الغذائية.",
        "لا توصي الشركة أو تؤيد أي فحوصات أو أطباء أو منتجات أو إجراءات أو آراء أو معلومات أخرى قد يتم ذكرها على الموقع الإلكتروني (بما في تلك التي يروجها أي معلنين أو رعاة إعلانات).",
        "على الرغم من أننا نبذل جهودًا معقولة لتحديث المعلومات على موقعنا الإلكتروني، إلا أننا لا نقدم أي تصريحات أو ضمانات أو ضمانات، سواء كانت صريحة أو ضمنية، بأن المحتوى على موقعنا الإلكتروني دقيق أو كامل أو حديث.",
        "يعتمد استخدام أي معلومات مقدمة على الموقع الإلكتروني على مسؤوليتك الخاصة فقط. نحن أيضًا لا نضمن أو نعلن أن المعلومات أو المواد المتاحة عبر الموقع الإلكتروني ستلبي متطلباتك أو احتياجاتك الخاصة أو أن الوصول إليها أو استخدامها سيكون دون انقطاع أو آمنًا تمامًا.",
        "إذا كنت ترغب في استخدام أي محتوى على موقعنا الإلكتروني بخلاف ما ورد أعلاه، فيرجى الاتصال بنا عبر زر 'اتصل بنا'.",
        "تم الحصول على قائمة 'المزيد من المعلومات' في البرنامج التي تحتوي على معلومات محددة حول كل منتج كما هي من صفحة المنتج على موقع الشركة المنتجة. لا يمكننا قبول أي مسؤولية regarding أي عدم دقة علمية في المعلومات الواردة هناك.",
        "قد يحتوي المحتوى الآخر الذي ننتجه على الموقع الإلكتروني (بما في ذلك تطبيق الويب والتطبيق المحمول) على عدم دقة فنية أو أخطاء مطبعية. نحتفظ بالحق في إجراء تغييرات وتحسينات على أي معلومات على الموقع الإلكتروني في أي وقت ودون إشعار.",
      ],

      // Accessing the Website or the App
      accessTitle: "الوصول إلى الموقع الإلكتروني أو التطبيق",
      accessItems: [
        "لا نضمن أن موقعنا الإلكتروني، أو أي محتوى عليه، سيكون دائمًا متاحًا أو أن الوصول إليه سيكون دون انقطاع.",
        "يجوز لنا تعليق أو سحب أو إنهاء أو تغيير كل أو أي جزء من موقعنا الإلكتروني دون إشعار.",
        "لن نكون مسؤولين تجاهك إذا تمت إزالة موقعنا الإلكتروني (أو أي جزء أو ميزات أو خدمات تحت الموقع الإلكتروني) أو أصبح غير متاح في أي وقت أو لأي فترة لأي سبب كان.",
        "سيتم إخطار المستخدمين المسجلين بالبريد الإلكتروني إذا كان مثل هذا الانقطاع سيؤثر على الخدمة التي اشتركوا فيها.",
        "أنت مسؤول عن اتخاذ جميع الترتيبات اللازمة للوصول إلى موقعنا الإلكتروني (بما في ذلك وجود اتصال بالإنترنت ومتصفح مناسب).",
        "أنت مسؤول أيضًا عن التأكد من أن كل من يصل إلى موقعنا الإلكتروني من خلال اتصالك بالإنترنت على علم بشروط الاستخدام هذه والشروط والأحكام الأخرى المعمول بها، وأنهم يمتثلون لها.",
      ],

      // Prohibited Uses
      prohibitedTitle: "الاستخدامات المحظورة",
      prohibitedIntro:
        "يمكنك استخدام موقعنا الإلكتروني والخدمات المرتبطة به فقط للأغراض القانونية. لا يجوز لك استخدام موقعنا الإلكتروني:",
      prohibitedItems: [
        "بأي طريقة تنتهك شروط الاستخدام هذه أو أي قوانين أو لوائح معمول بها.",
        "بأي طريقة غير قانونية أو احتيالية، أو لها أي غرض أو تأثير غير قانوني أو احتيالي.",
        "لاستخدام أو إعادة استخدام أي مادة لأي غرض تجاري أو بطريقة لا تتوافق مع بيان شروط الاستخدام الخاص بنا.",
        "لنقل، أو التسبب في إرسال، أي إعلانات أو مواد ترويجية غير مرغوب فيها أو غير مصرح بها أو أي شكل آخر من الطلبات المماثلة (المعروفة باسم 'البريد المزعج').",
        "لنقل أي بيانات عن علم، أو إرسال أو تحميل أي مادة تحتوي على فيروسات، أو أحصنة طروادة، أو ديدان، أو قنابل موقوتة، أو مسجلات ضغطات المفاتيح، أو برامج تجسس، أو برامج إعلانية، أو أي برامج ضارة أخرى أو كود كمبيوتر مماثل مصمم للتأثير سلبًا على تشغيل أي برنامج أو أجهزة كمبيوتر.",
      ],

      // Disclaimers
      disclaimersTitle: "إخلاء المسؤولية",
      disclaimersItems: [
        "يحتوي الموقع الإلكتروني على محتوى ننتجه (أو نيابة عنا من قبل مؤلفين مؤهلين علميًا). محتوى الموقع الإلكتروني هو لأغراض المعلومات العامة فقط. لم يتم إعداده لتقديم نصيحة محددة يجب أن تعتمد عليها.",
        "على وجه الخصوص، لا يعد الموقع الإلكتروني (والمعلومات التي يمكن الوصول إليها من خلاله) بديلاً عن المشورة الطبية المهنية من طبيب مؤهل، أو أخصائي تغذية سريري، أو غيرهم من متخصصي الرعاية الصحية.",
        "بصفتك أخصائي تغذية سريري، فأنت ملزم باستشارة النتائج التي يولدها تطبيقنا الويب مع فريقك من أخصائيي التغذية والأطباء.",
        "إذا كنت متخصصًا في الرعاية الصحية، فإن هذه المعلومات (بما في ذلك أي مواد مرجعية مهنية أو خطط نظام غذائي مقترحة) تهدف إلى دعم، وليس استبدال، معرفتك وخبرتك وتقديرك الخاص.",
      ],

      // Intellectual Property Rights
      ipTitle: "حقوق الملكية الفكرية",
      ipItems: [
        "نحن المالكون لجميع حقوق الملكية الفكرية في موقعنا الإلكتروني، والمادة المنشورة عليه، والخدمات التي يتم الوصول إليها من خلاله. تحمي قوانين ومعاهدات حقوق الطبع والنشر تلك الأعمال في المملكة العربية السعودية.",
        "يمكنك تنزيل و/أو طباعة نسخة واحدة من مقتطفات أي صفحة (صفحات) من موقعنا الإلكتروني لاستخدامك المهني، ويمكنك (في سياق غير تجاري) لفت انتباه الآخرين إلى المحتوى المنشور على موقعنا الإلكتروني.",
        "يجب ألا تعدل النسخ الورقية أو الرقمية من أي مواد قمت بطباعتها أو تنزيلها بأي طريقة، ويجب ألا تستخدم أي رسوم توضيحية أو شعارات أو صور أو تسلسلات فيديو أو صوتية أو أي رسومات بشكل منفصل عن أي نص مصاحب.",
        "يجب ألا تستخدم أي جزء من المحتوى على موقعنا الإلكتروني لأغراض تجارية دون الحصول على ترخيص منا للقيام بذلك.",
        "إذا قمت بطباعة أو نسخ أو تنزيل أي جزء من موقعنا الإلكتروني بما يخالف شروط الاستخدام هذه، فسيتوقف حقك في استخدام موقعنا الإلكتروني (وأي مواد ذات صلة) على الفور. يجب عليك، وفقًا لاختيارنا، إرجاع أو تدمير أي نسخ من المواد التي قمت بعملها (وستستمر شروط الاستخدام هذه في التطبيق regarding أي من هذه المواد).",
      ],

      // Registering for an Account
      accountTitle: "التسجيل للحصول على حساب",
      accountItems: [
        "إذا اخترت التسجيل للحصول على حساب على موقعنا الإلكتروني، واخترت أو تم منحك رمز تعريف المستخدم، أو كلمة مرور، أو أي جزء آخر من المعلومات كجزء من إجراءات التسجيل أو الأمان الخاصة بنا، فيجب عليك معاملة هذه المعلومات على أنها سرية. يجب ألا تفصح عنها لأي شخص.",
        "يحق لنا إلغاء تنشيط أي رمز تعريف مستخدم أو كلمة مرور، سواء تم اختياره من قبلك أو تخصيصه من قبلنا، في أي وقت (وبالتالي إزالة قدرتك على استخدام خدماتنا) وفقًا لتقديرنا، بما في ذلك، على سبيل المثال لا الحصر، في الظروف التي، في رأينا المعقول، فشلت في الامتثال لأي من أحكام شروط الاستخدام هذه.",
        "إذا كنت تعلم أو تشك في أن أي شخص غيرك لديه حق الوصول إلى رمز تعريف المستخدم أو كلمة المرور الخاصين بك، فيجب عليك إعادة تعيين كلمة المرور الخاصة بك على الفور إلى شيء لا يعرفه سواك.",
        "يرجى الاطلاع على سياسة الخصوصية الخاصة بنا لمزيد من المعلومات regarding استخدام البيانات الشخصية المتعلقة بك.",
        "يجب عليك استخدام الموقع الإلكتروني فقط للأغراض القانونية، ويجب ألا تنتهك حقوق المستخدمين الآخرين لهذا الموقع أو تمنع تمتعهم به.",
      ],

      // Suspension and Termination
      suspensionTitle: "التعليق وإنهاء الوصول",
      suspensionIntro:
        "سنحدد، وفقًا لتقديرنا، ما إذا كان هناك انتهاك لشروط الاستخدام الخاصة بنا من خلال استخدامك لموقعنا الإلكتروني. عندما يحدث انتهاك لهذه الشروط (في رأينا)، أو حيث نعتبر ذلك ضروريًا بخلاف ذلك، فقد نتخذ مثل هذا الإجراء الذي نعتبره مناسبًا، والذي قد يشمل اتخاذ أي أو كل الإجراءات التالية:",
      suspensionItems: [
        "إصدار تحذير إلى مؤسستك.",
        "سحب فوري أو مؤقت أو دائم لحقك في استخدام تطبيقنا الويب والتطبيق المحمول.",
        "إجراءات قانونية ضدك لتعويض جميع التكاليف على أساس التعويض (بما في ذلك، على سبيل المثال لا الحصر، التكاليف الإدارية والقانونية المعقولة) الناتجة عن الانتهاك.",
        "الكشف عن such information إلى سلطات إنفاذ القانون كما نعتقد بشكل معقول أنه ضروري.",
      ],
      suspensionEnd:
        "نستبعد المسؤولية عن الإجراءات المتخذة ردًا على انتهاكات سياسة الاستخدام المقبولة هذه. الردود الموصوفة في هذه السياسة هي أمثلة، وقد نتخذ مثل هذه الإجراءات الأخرى التي قد نعتبرها معقولة ومناسبة.",

      // Contributions
      contributionsTitle: "المساهمات في تطبيقنا الويب",
      contributionsItems: [
        "أي مساهمة في تطبيق الويب، بما في ذلك تقديم أمراض وأدوية جديدة، فإنك تمنحنا حقًا دائمًا وخاليًا من الإتلافات وحقًا غير حصري لتخزين وتعديل (بعد التحقق من الدقة العلمية لتقديمك) ونسخ ونشر المساهمة على تطبيق الويب والتطبيق المحمول وجعلها متاحة للمستخدمين الآخرين.",
        "يحق لنا إزالة أي منشور تقوم به على تطبيقنا الويب إذا لم يكن منشورك مدعومًا بأدلة علمية قوية، أو لم يتوافق مع معايير المحتوى، أو أي شروط منصوص عليها في هذه الاتفاقية.",
      ],

      // Third-Party Links
      linksTitle: "روابط وموارد الطرف الثالث على موقعنا الإلكتروني",
      linksItems: [
        "قد يحتوي الموقع الإلكتروني على روابط لمواقع إلكترونية لا تحافظ عليها شركتنا. يتم توفير روابط لمواقع الطرف الثالث لراحتك ومعلوماتك فقط.",
        "مواقع الطرف الثالث ليست تحت سيطرة شركتنا، ونحن لسنا مسؤولين عن محتوى أو دقة تلك المواقع، أو عن المنتجات أو الخدمات المقدمة على أو من خلالها.",
        "لا يعني تضمين رابط على الموقع الإلكتروني تأييدنا لموقع الطرف الثالث، ولا يعني أن شركتنا تابعة لملاك أو رعاة موقع الطرف الثالث.",
        "أي معلومات أو محتوى تجعله شركتنا متاحًا على هذا الموقع الإلكتروني من أطراف ثالثة ('مواد الطرف الثالث') يتم توفيره لأغراض المعلومات العامة فقط.",
        "لا تؤيد الشركة مواد الطرف الثالث وتتخلى عن أي مسؤولية وتتنصل من جميع المسؤولية فيما يتعلق بمواد الطرف الثالث such.",
      ],

      // Limitations of Liability
      liabilityTitle: "قيود المسؤولية",
      liabilityIntro:
        "يرجى قراءة هذا القسم بعناية حيث أنه يحدد حدود مسؤوليتنا تجاهك فيما يتعلق باستخدامك لـ iNutriCal (بما في ذلك الموقع الإلكتروني، وتطبيق الويب، والتطبيق المحمول).",
      liabilityImportant:
        "لا شيء في شروط الاستخدام هذه يستبعد أو يحد من مسؤوليتنا عن الوفاة أو الإصابة الشخصية الناشئة عن إهمالنا، أو عن الاحتيال أو التضليل الاحتيالي الخاص بنا، أو عن أي مسؤولية أخرى لا يمكن استبعادها أو تحديدها بموجب قانون المملكة العربية السعودية.",
      liabilityNote:
        "يرجى ملاحظة أننا نقدم منتجنا كأداة مرجعية ودعم لاستخدام متخصصي الرعاية الصحية فقط.",
      liabilityItems: [
        "إلى الحد المسموح به بموجب القانون، نستبعد جميع الشروط والضمانات والتصريحات أو الشروط الأخرى التي قد تنطبق على الموقع الإلكتروني أو أي محتوى عليه، سواء كانت صريحة أو ضمنية.",
        "أنت توافق على عدم استخدام الموقع الإلكتروني (أو أي من محتواه) لأي أغراض تجارية أو أعمال، وليس لدينا أي مسؤولية تجاهك عن أي خسارة في الأرباح، أو خسارة في الأعمال، أو انقطاع في الأعمال، أو فقدان فرصة تجارية.",
        "نحن لا نتحمل أي مسؤولية عن محتوى المواقع الإلكترونية المرتبطة على موقعنا الإلكتروني. لا يجب تفسير such links على أنها تأييد منا لتلك المواقع الإلكترونية المرتبطة. لن نكون مسؤولين عن أي خسارة أو ضرر قد ينشأ من استخدامك لها.",
        "نبذل جهودنا المعقولة للتحقق من دقة المعلومات المهنية المنشورة على الموقع الإلكتروني. يجب أن تلاحظ أننا لا نضمن أن such information ستكون خالية من الأخطاء، وأنك تقر بأن المعلومات والمنتجات والخدمات المنشورة على هذا الموقع الإلكتروني، أو تطبيق الويب، أو التطبيق المحمول قد تشمل عدم دقة أو أخطاء مطبعية.",
        "يتم إجراء تغييرات بشكل دوري على المعلومات على موقعنا الإلكتروني. يجب أن تعتمد دائمًا على حكمك الخبير في التعامل مع المرضى. إذا لاحظت أي such عدم دقة أو لديك اقتراحات علمية، فمن مسؤوليتك المطلقة إخطارنا في أقرب وقت ممكن عبر زر 'اتصل بنا'.",
        "لن نكون مسؤولين عن أي خسارة أو ضرر ناتج عن فيروس، أو هجوم حجب الخدمة الموزع، أو أي مادة ضارة تكنولوجيًا أخرى قد تصيب معدات الكمبيوتر أو برامج الكمبيوتر أو البيانات أو المواد الخاصة الأخرى due to استخدامك لموقعنا الإلكتروني أو due to تنزيلك لأي محتوى عليه، أو على أي موقع إلكتروني مرتبط به.",
        "لن نكون مسؤولين أيضًا عن other القضايا التكنولوجية التي قد تؤثر على وصولك إلى خدماتنا، مثل عدم توفر الخوادم المتفق عليها أو خدمة الإنترنت.",
      ],
      liabilityEnd:
        "لا يؤثر هذا القسم على أي حقوق قانونية قد تكون لديك كـ مستهلك. أنت تقر بأننا قد جعلنا الموقع الإلكتروني متاحًا لك اعتمادًا على شروط الاستخدام these و، على وجه الخصوص، these قيود المسؤولية.",

      // Collection of Personal Data
      dataTitle: "جمع البيانات الشخصية",
      dataItems: [
        "إذا قمت بالتسجيل لاستخدام تطبيقنا الويب، أو أي خدمة (خدمات) يمكن الوصول إليها through موقعنا الإلكتروني، فقد نجمع بيانات شخصية منك (بما في ذلك، على سبيل المثال لا الحصر، اسمك، ورقم هاتف الاتصال، وعنوان بريد إلكتروني صالح).",
        "سيتم تخزين these data بشكل آمن وفقًا لسياسة الخصوصية الخاصة بنا.",
        "سيتم تخزين بيانات المرضى الشخصية والطبية بشكل آمن وفقًا لسياسة الخصوصية الخاصة بنا.",
        "إذا كان أي أطراف ثالثة involved في تقديم أو تشغيل any services قد تختار تلقيها، فسيتم تفصيل this في المعلومات والشروط المقدمة لك في وقت توقيع العقد.",
      ],

      // Viruses
      virusesTitle: "الفيروسات",
      virusesItems: [
        "لا نضمن أن موقعنا الإلكتروني سيكون آمنًا أو خاليًا من الأخطاء أو الفيروسات، ويجب عليك استخدام برنامج مكافحة الفيروسات الخاص بك المحدث.",
        "يجب ألا تسيء استخدام موقعنا الإلكتروني عن طريق إدخال فيروسات، أو أحصنة طروادة، أو ديدان، أو قنابل منطقية، أو أي مادة أخرى خبيثة أو ضارة technologically عن علم.",
        "يجب ألا تحاول الحصول على وصول غير مصرح به إلى موقعنا الإلكتروني، أو الخادم الذي يتم عليه تخزين موقعنا الإلكتروني، أو أي خادم، أو كمبيوتر، أو قاعدة بيانات connected to موقعنا الإلكتروني أو التطبيقات associated with it.",
        "يجب ألا تهاجم موقعنا الإلكتروني عبر هجوم حجب الخدمة أو هجوم حجب الخدمة الموزع.",
        "بمخالفتك لهذا الحكم، قد ترتكب جريمة بموجب قانون هيئة الاتصالات وتقنية المعلومات، المملكة العربية السعودية.",
        "سوف نبلغ عن any such breach إلى سلطات إنفاذ القانون ذات الصلة وسنتعاون معهم من خلال الكشف عن هويتك. في حالة such breach، سيتوقف حقك في استخدام موقعنا الإلكتروني على الفور.",
      ],

      // General
      generalTitle: "أحكام عامة",
      generalItems: [
        "إذا تم تحديد أن أي جزء من شروط الاستخدام these غير صالح أو غير قابل للتنفيذ pursuant to القانون المعمول به including، على سبيل المثال لا الحصر، إخلاء المسؤولية and قيود المسؤولية أعلاه، then سيتم اعتبار الحكم غير الصالح أو غير القابل للتنفيذ مستبدلاً بحكم صالح وقابل للتنفيذ matches بشكل أقرب نية الحكم الأصلي and سيستمر باقي الاتفاقية ساري المفعول.",
        "نحتفظ بالحق في تعيين، أو نقل، بحرية، any of حقوقنا أو التزاماتنا under شروط الاستخدام these إلى any طرف ثالث at تقديرنا.",
        "لن نكون في خرق any of التزاماتنا under شروط الاستخدام these (أو مسؤولين بخلاف ذلك عن any فشل أو تأخير in الأداء) if تم منعنا، أو عرقلة، أو تأخير in، or from، أداء any of التزاماتنا by any event beyond سيطرتنا المعقولة. سيتم تمديد وقت أداء such obligations accordingly.",
        "Subject to any شروط محددة مفصلة on الموقع الإلكتروني in relation to ميزات أو مواد معينة، تشكل شروط الاستخدام these (مع سياسة الخصوصية) الاتفاقية الكاملة between you and us in respect of استخدامك لموقعنا الإلكتروني أو خدماتنا.",
        "Any حقوق not منحت expressly herein محفوظة.",
      ],

      // Contact Us
      contactTitle: "اتصل بنا",
      contactContent:
        "للاتصال بنا، يرجى استخدام نموذج اتصل بنا على موقع iNutriCal الإلكتروني.",
    },
  };

  const current = content[language];

  return (
    <div
      className="min-h-screen bgH text-white px-2"
      dir={language === "arabic" ? "rtl" : "ltr"}
    >
      {/* Header */}
      <header className="">
        <div className="container mx-auto py-4">
          <div className="flex justify-between gap-5 max-sm:items-center items-start">
            {/* Home Button */}
            <a href="/" className="block mx-auto">
              <img
                src="/images/logo.png"
                alt="Logo"
                className={`max-w-[300px] cursor-pointer relative z-10 h-auto max-[800px]:w-full`}
              />
            </a>

            {/* Language Switcher */}
            <div className="language-switcher relative z-50">
              <div className="w-10 h-8 bg-white/20 font-inter text-white rounded text-sm relative overflow-hidden transition-all duration-500">
                {/* English */}
                <button
                  onClick={() => setLanguage("english")}
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
                    language === "arabic"
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 -translate-y-full"
                  }`}
                >
                  EN
                </button>

                {/* Arabic */}
                <button
                  onClick={() => setLanguage("arabic")}
                  className={`absolute inset-0 flex items-center justify-center transition-all duration-500 ${
                    language === "english"
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-full"
                  }`}
                >
                  AR
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Terms of Use Content */}
      <div className="container mx-auto font-inter max-w-4xl pb-10">
        <div className="md:p-8" data-aos="fade-up">
          {/* Title */}
          <h1 className="text-3xl md:text-4xl font-bold hero-heading mb-2">
            {current.title}
          </h1>

          {/* Last Updated */}
          <div className="mb-8 text-white/70 text-xs">
            {current.lastUpdated} {current.effectiveDate}
          </div>

          {/* Content Container */}
          <div
            className={`prose prose-invert max-w-none prose-headings:text-white/70 prose-p:text-gray-300 prose-li:text-gray-300 prose-strong:text-white prose-a:text-purple-400 ${
              language === "arabic" ? "text-right" : "text-left"
            }`}
          >
            {/* Introduction */}
            <div data-aos="fade-up" data-aos-delay="100">
              <p className="leading-relaxed mb-4 font-medium hero-heading">
                {current.intro1}
              </p>
              <p className="leading-relaxed mb-4 font-medium hero-heading">
                {current.intro2}
              </p>
              <p className="leading-relaxed mb-4 font-medium hero-heading">
                {current.intro3}
              </p>
              <p className="leading-relaxed mb-4 font-medium hero-heading">
                {current.intro4}
              </p>
            </div>

            {/* Warning Notice */}
            <div
              data-aos="fade-up"
              data-aos-delay="150"
              className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4 max-sm:p-2 my-4"
            >
              <p className="text-yellow-200 max-sm:text-sm font-medium text-center">
                {current.warning}
              </p>
            </div>

            {/* Content Section */}
            <section data-aos="fade-up" data-aos-delay="200" className="my-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.contentTitle}
              </h2>
              <ul
                className={`space-y-3 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.contentItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Accessing the Website or the App */}
            <section data-aos="fade-up" data-aos-delay="250" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.accessTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.accessItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Prohibited Uses */}
            <section data-aos="fade-up" data-aos-delay="300" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.prohibitedTitle}
              </h2>
              <p className="leading-relaxed mb-2 text-[#CFB997]">
                {current.prohibitedIntro}
              </p>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.prohibitedItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Disclaimers */}
            <section data-aos="fade-up" data-aos-delay="350" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.disclaimersTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.disclaimersItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Intellectual Property Rights */}
            <section data-aos="fade-up" data-aos-delay="400" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.ipTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.ipItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Registering for an Account */}
            <section data-aos="fade-up" data-aos-delay="450" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.accountTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.accountItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Suspension and Termination */}
            <section data-aos="fade-up" data-aos-delay="500" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.suspensionTitle}
              </h2>
              <p className="leading-relaxed mb-2 text-[#CFB997]">
                {current.suspensionIntro}
              </p>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.suspensionItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
              <p className="leading-relaxed hero-heading">
                {current.suspensionEnd}
              </p>
            </section>

            {/* Contributions */}
            <section data-aos="fade-up" data-aos-delay="550" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.contributionsTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.contributionsItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Third-Party Links */}
            <section data-aos="fade-up" data-aos-delay="600" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.linksTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.linksItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Limitations of Liability */}
            <section data-aos="fade-up" data-aos-delay="650" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.liabilityTitle}
              </h2>
              <p className="leading-relaxed mb-4 hero-heading">
                {current.liabilityIntro}
              </p>
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 mb-4">
                <p className="text-red-200 font-semibold hero-heading">
                  {current.liabilityImportant}
                </p>
              </div>
              <p className="leading-relaxed mb-4 text-[#CFB997]">
                {current.liabilityNote}
              </p>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.liabilityItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
              <p className="leading-relaxed hero-heading">
                {current.liabilityEnd}
              </p>
            </section>

            {/* Collection of Personal Data */}
            <section data-aos="fade-up" data-aos-delay="700" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.dataTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.dataItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Viruses */}
            <section data-aos="fade-up" data-aos-delay="750" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.virusesTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.virusesItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* General */}
            <section data-aos="fade-up" data-aos-delay="800" className="mb-8">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.generalTitle}
              </h2>
              <ul
                className={`space-y-2 mb-4 ${
                  language === "arabic" ? "pr-4" : "pl-4"
                }`}
              >
                {current.generalItems.map((item, index) => (
                  <li
                    key={index}
                    className="leading-relaxed hero-heading font-medium"
                  >
                    <span className="text-xl bg-white/70 mr-1 size-2 inline-block rounded-full"></span>{" "}
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Contact Us */}
            <section data-aos="fade-up" data-aos-delay="850">
              <h2 className="text-3xl font-semibold text-[#FAD6A5] max-sm:text-xl mt-3 mb-4 border-b border-white/10 pb-2">
                {current.contactTitle}
              </h2>
              <p className="leading-relaxed hero-heading">
                {current.contactContent}
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfUse;
